import { stringifyLosslessJson } from '@anchorbase/pillar-sdk';
import type { ReactNode } from 'react';
import {
    CardPointeIpgSystemId,
    CardPointeIpgSystemIdKey,
    CloverIpgSystemId,
    CloverIpgSystemIdKey,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    type IpgSystemId,
    type IpgSystemIdKey,
    type IpgConfigCustomization,
    type IpgConfigCustomizationMap,
    type MerchantConfig,
} from '@anchorbase/pillar-sdk';
import {
    ValorIpgSystemId,
    ValorIpgSystemIdKey,
} from '@anchorbase/pillar-sdk/ipg/valor/system-constants';

import {
    CardPointeOrganizationIpgConfigEditorAdapter,
    type CardPointeOrganizationIpgConfigEditorState,
} from './cardpointe/CardPointeOrganizationIpgConfigEditor';
import {
    CloverOrganizationIpgConfigEditorAdapter,
    type CloverOrganizationIpgConfigEditorState,
} from './clover/CloverOrganizationIpgConfigEditor';
import {
    ValorOrganizationIpgConfigEditorAdapter,
    type ValorOrganizationIpgConfigEditorState,
} from './valor/ValorOrganizationIpgConfigEditor';

export type OrganizationIpgConfigEditorState =
    | ValorOrganizationIpgConfigEditorState
    | CardPointeOrganizationIpgConfigEditorState
    | CloverOrganizationIpgConfigEditorState;

export type OrganizationIpgConfigEditorStateMap = Readonly<
    Record<IpgSystemIdKey, OrganizationIpgConfigEditorState>
>;

export function buildOrganizationIpgConfigEditorStateMap(
    merchantConfig: MerchantConfig | undefined
): OrganizationIpgConfigEditorStateMap {
    return {
        [ValorIpgSystemIdKey]:
            ValorOrganizationIpgConfigEditorAdapter.createEditableState({
                merchantConfig,
                defaultConfig: defaultIpgConfigByKey[ValorIpgSystemIdKey],
                demoConfig: defaultDemoIpgConfigByKey[ValorIpgSystemIdKey],
            }),
        [CardPointeIpgSystemIdKey]:
            CardPointeOrganizationIpgConfigEditorAdapter.createEditableState({
                merchantConfig,
                defaultConfig:
                    defaultIpgConfigByKey[CardPointeIpgSystemIdKey],
                demoConfig:
                    defaultDemoIpgConfigByKey[CardPointeIpgSystemIdKey],
            }),
        [CloverIpgSystemIdKey]:
            CloverOrganizationIpgConfigEditorAdapter.createEditableState({
                merchantConfig,
                defaultConfig: defaultIpgConfigByKey[CloverIpgSystemIdKey],
                demoConfig:
                    defaultDemoIpgConfigByKey[CloverIpgSystemIdKey],
            }),
    };
}

export function buildOrganizationIpgConfigEditorSavePayloadOrThrow(
    editorState: OrganizationIpgConfigEditorState
): IpgConfigCustomization {
    switch (editorState.ipgSystemId) {
        case ValorIpgSystemId:
            return ValorOrganizationIpgConfigEditorAdapter.buildSavePayload({
                editorState,
            });
        case CardPointeIpgSystemId:
            return CardPointeOrganizationIpgConfigEditorAdapter.buildSavePayload({
                editorState,
            });
        case CloverIpgSystemId:
            return CloverOrganizationIpgConfigEditorAdapter.buildSavePayload({
                editorState,
            });
        default: {
            const exhaustiveState: never = editorState;
            throw new Error(
                `Unsupported IPG config editor state: ${stringifyLosslessJson(exhaustiveState)}`
            );
        }
    }
}

export function buildOrganizationIpgConfigCustomizationMapOrThrow(input: {
    readonly enabledIpgSystemIds: readonly IpgSystemId[];
    readonly editorStateMap: OrganizationIpgConfigEditorStateMap;
    readonly policyCustomizationMap: IpgConfigCustomizationMap;
}): IpgConfigCustomizationMap {
    const customizationMap: IpgConfigCustomizationMap = {};
    for (const ipgSystemId of input.enabledIpgSystemIds) {
        switch (ipgSystemId) {
            case ValorIpgSystemId: {
                const editorState = input.editorStateMap[ValorIpgSystemIdKey];
                const policyCustomization =
                    input.policyCustomizationMap[ValorIpgSystemIdKey];
                if (editorState.ipgSystemId !== ValorIpgSystemId) {
                    throw new Error('Valor editor state does not match its map key.');
                }
                if (policyCustomization?.ipg_id !== ValorIpgSystemId) {
                    throw new Error(
                        'Valor policy customization does not match its map key.'
                    );
                }
                const providerPayload =
                    ValorOrganizationIpgConfigEditorAdapter.buildSavePayload({
                        editorState,
                    });
                customizationMap[ValorIpgSystemIdKey] = {
                    ...providerPayload,
                    ...(providerPayload.configuration_mode === 'CUSTOM'
                        ? {
                              urlPattern: policyCustomization.urlPattern,
                              allowedWebsites: policyCustomization.allowedWebsites,
                          }
                        : {}),
                };
                break;
            }
            case CardPointeIpgSystemId: {
                const editorState =
                    input.editorStateMap[CardPointeIpgSystemIdKey];
                const policyCustomization =
                    input.policyCustomizationMap[CardPointeIpgSystemIdKey];
                if (editorState.ipgSystemId !== CardPointeIpgSystemId) {
                    throw new Error(
                        'CardPointe editor state does not match its map key.'
                    );
                }
                if (policyCustomization?.ipg_id !== CardPointeIpgSystemId) {
                    throw new Error(
                        'CardPointe policy customization does not match its map key.'
                    );
                }
                const providerPayload =
                    CardPointeOrganizationIpgConfigEditorAdapter.buildSavePayload({
                        editorState,
                    });
                customizationMap[CardPointeIpgSystemIdKey] = {
                    ...policyCustomization,
                    environment: providerPayload.environment,
                    portalUrl: providerPayload.allowedWebsites[0]?.url,
                    urlPattern: providerPayload.urlPattern,
                    allowedWebsites: policyCustomization.allowedWebsites.map(
                        (policyWebsite, websiteIndex) => ({
                            ...policyWebsite,
                            allowedUrlPatterns:
                                providerPayload.allowedWebsites[websiteIndex]
                                    ?.allowedUrlPatterns ??
                                policyWebsite.allowedUrlPatterns,
                        })
                    ),
                };
                break;
            }
            case CloverIpgSystemId: {
                const editorState = input.editorStateMap[CloverIpgSystemIdKey];
                const policyCustomization =
                    input.policyCustomizationMap[CloverIpgSystemIdKey];
                if (editorState.ipgSystemId !== CloverIpgSystemId) {
                    throw new Error('Clover editor state does not match its map key.');
                }
                if (policyCustomization?.ipg_id !== CloverIpgSystemId) {
                    throw new Error(
                        'Clover policy customization does not match its map key.'
                    );
                }
                const providerPayload =
                    CloverOrganizationIpgConfigEditorAdapter.buildSavePayload({
                        editorState,
                    });
                customizationMap[CloverIpgSystemIdKey] = {
                    ipg_id: CloverIpgSystemId,
                    environment: providerPayload.environment,
                };
                break;
            }
            default: {
                const exhaustiveIpgSystemId: never = ipgSystemId;
                throw new Error(
                    `Unsupported IPG config editor adapter: ${exhaustiveIpgSystemId}`
                );
            }
        }
    }
    return customizationMap;
}

export function renderOrganizationIpgConfigEditor(input: {
    readonly organizationUuid: string;
    readonly editorState: OrganizationIpgConfigEditorState;
    readonly onChange: (
        nextState: OrganizationIpgConfigEditorState
    ) => void;
    readonly disabled: boolean;
}): ReactNode {
    switch (input.editorState.ipgSystemId) {
        case ValorIpgSystemId:
            return ValorOrganizationIpgConfigEditorAdapter.renderEditable({
                organizationUuid: input.organizationUuid,
                editorState: input.editorState,
                onChange: (nextState) => input.onChange(nextState),
                disabled: input.disabled,
            });
        case CardPointeIpgSystemId:
            return CardPointeOrganizationIpgConfigEditorAdapter.renderEditable({
                organizationUuid: input.organizationUuid,
                editorState: input.editorState,
                onChange: (nextState) => input.onChange(nextState),
                disabled: input.disabled,
            });
        case CloverIpgSystemId:
            return CloverOrganizationIpgConfigEditorAdapter.renderEditable({
                organizationUuid: input.organizationUuid,
                editorState: input.editorState,
                onChange: (nextState) => input.onChange(nextState),
                disabled: input.disabled,
            });
        default: {
            const exhaustiveState: never = input.editorState;
            throw new Error(
                `Unsupported IPG config editor state: ${stringifyLosslessJson(exhaustiveState)}`
            );
        }
    }
}

export function renderOrganizationIpgReadOnlySummary(input: {
    readonly ipgSystemId: IpgSystemId;
    readonly organizationUuid: string;
    readonly merchantConfig: MerchantConfig | undefined;
}): ReactNode {
    switch (input.ipgSystemId) {
        case ValorIpgSystemId:
            return ValorOrganizationIpgConfigEditorAdapter.renderReadOnlySummary(
                {
                    organizationUuid: input.organizationUuid,
                    merchantConfig: input.merchantConfig,
                    defaultConfig:
                        defaultIpgConfigByKey[ValorIpgSystemIdKey],
                }
            );
        case CardPointeIpgSystemId:
            return CardPointeOrganizationIpgConfigEditorAdapter.renderReadOnlySummary(
                {
                    organizationUuid: input.organizationUuid,
                    merchantConfig: input.merchantConfig,
                    defaultConfig:
                        defaultIpgConfigByKey[CardPointeIpgSystemIdKey],
                }
            );
        case CloverIpgSystemId:
            return CloverOrganizationIpgConfigEditorAdapter.renderReadOnlySummary(
                {
                    organizationUuid: input.organizationUuid,
                    merchantConfig: input.merchantConfig,
                    defaultConfig:
                        defaultIpgConfigByKey[CloverIpgSystemIdKey],
                }
            );
        default: {
            const exhaustiveIpgSystemId: never = input.ipgSystemId;
            throw new Error(
                `Unsupported IPG config editor adapter: ${exhaustiveIpgSystemId}`
            );
        }
    }
}
